import React, { useState, useEffect } from "react";

// Custom Components
import LoginHeader from "../components/LoginHeader.js";
import SideNavigation from "../components/SideNavigation.js";
import AADHeader from "../components/AADHeader.js";

// Material-UI Components
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Button from "@mui/material/Button";
import WestIcon from "@mui/icons-material/West";
import Typography from "@mui/material/Typography";
import { Backdrop, CircularProgress } from "@mui/material";

// Third-party Components
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useNavigate } from "react-router-dom";
import "./brsr.css";
import { getSavedReportName } from "../utils/getSavedReportName.js";

const steps = ["Start", "Repository", "Input", "Report"];

// BRSR Sections Data with all accordion tabs
const BRSR_ACCORDION_SECTIONS = ["Principle 1", "Principle 2", "Principle 3", "Principle 4", "Principle 5", "Principle 6", "Principle 7", "Principle 8", "Principle 9"];

export default function BrsrDashboard() {
  const username = localStorage.getItem("userName");
  const companyid = localStorage.getItem("companyid");
  const navigate = useNavigate();
  const savedReportName = getSavedReportName();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [reportInfo, setReportInfo] = useState({
    reportName: "BRSR Report 2026",
    completionDate: "10 Feb 2024",
  });
  const [drStats, setDrStats] = useState([]);
  const [dataStatus, setDataStatus] = useState({
    reported: 65,
    notMaterial: 25,
    toDo: 10,
  });
  const [dataCollection, setDataCollection] = useState({
    quantitativeAnswered: 65,
    qualitativeAnswered: 35,
  });
  const [quantitativeDataSources, setQuantitativeDataSources] = useState({
    automatedInputs: 400,
    manualInputs: 820,
    notAvailable: 362,
    totalCells: 1582,
  });
  const [esgBreakdown, setEsgBreakdown] = useState({
    SectionA: { toDo: 10, notMaterial: 20, reported: 70 },
    SectionB: { toDo: 15, notMaterial: 25, reported: 60 },
    SectionC: { toDo: 5, notMaterial: 15, reported: 80 },
    Governance: { toDo: 8, notMaterial: 18, reported: 74 },
  });

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // Fetch data from localStorage
    const step1 = localStorage.getItem("step1");
    const step4 = localStorage.getItem("step4");

    if (step1) {
      try {
        const data = JSON.parse(step1);
        setReportInfo((prev) => ({
          ...prev,
          reportName: data.name || data.reportName || getSavedReportName("BRSR Report 2026"),
        }));
      } catch (error) {
        console.error("Error parsing step1:", error);
      }
    }

    if (step4) {
      try {
        const data = JSON.parse(step4);

        // Calculate statistics
        const totalAnswered = data.filter((item) => item.answer && item.answer.trim() !== "").length;
        const total = data.length;

        // Create stats for each section and principle
        const stats = [
          { id: "section-a", label: "Section A: General Disclosures", answered: Math.floor(totalAnswered * 0.3), total: Math.floor(total * 0.3) },
          { id: "section-b", label: "Section B: Management And Process Disclosures", answered: Math.floor(totalAnswered * 0.35), total: Math.floor(total * 0.35) },
          { id: "section-c", label: "Section C: Principle-Wise Performance", answered: Math.floor(totalAnswered * 0.35), total: Math.floor(total * 0.35) },
          { id: "principle-1", label: "Principle 1", answered: Math.floor(totalAnswered * 0.11), total: Math.floor(total * 0.11) },
          { id: "principle-2", label: "Principle 2", answered: Math.floor(totalAnswered * 0.11), total: Math.floor(total * 0.11) },
          { id: "principle-3", label: "Principle 3", answered: Math.floor(totalAnswered * 0.11), total: Math.floor(total * 0.11) },
          { id: "principle-4", label: "Principle 4", answered: Math.floor(totalAnswered * 0.12), total: Math.floor(total * 0.12) },
          { id: "principle-5", label: "Principle 5", answered: Math.floor(totalAnswered * 0.12), total: Math.floor(total * 0.12) },
          { id: "principle-6", label: "Principle 6", answered: Math.floor(totalAnswered * 0.11), total: Math.floor(total * 0.11) },
          { id: "principle-7", label: "Principle 7", answered: Math.floor(totalAnswered * 0.11), total: Math.floor(total * 0.11) },
          { id: "principle-8", label: "Principle 8", answered: Math.floor(totalAnswered * 0.11), total: Math.floor(total * 0.11) },
          { id: "principle-9", label: "Principle 9", answered: Math.floor(totalAnswered * 0.05), total: Math.floor(total * 0.05) },
        ];

        setDrStats(stats);
      } catch (error) {
        console.error("Error parsing step4:", error);
      }
    }
  }, []);

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  // Highcharts configuration - Same as ReportDashboard
  const dataPointsChartOptions = {
    chart: {
      type: "pie",
      height: 200,
      events: {
        render: function () {
          const chart = this;
          const centerX = chart.plotWidth / 1.8 + chart.plotLeft;
          const centerY = chart.plotHeight / 2 + chart.plotTop;

          if (chart.customLabel) chart.customLabel.destroy();

          chart.customLabel = chart.renderer
            .text("1582", centerX, centerY)
            .css({
              color: "#000",
              fontSize: "16px",
              fontWeight: "500",
              textAlign: "right",
            })
            .attr({
              align: "center",
            })
            .add()
            .toFront();

          chart.customLabel.attr({
            x: centerX - chart.customLabel.getBBox().width / 2,
            y: centerY + chart.customLabel.getBBox().height / 4,
          });
        },
      },
    },
    title: {
      text: "Data Points Status",
      align: "left",
      margin: 30,
    },
    credits: {
      enabled: false,
    },
    legend: {
      align: "left",
      verticalAlign: "top",
      layout: "vertical",
      itemStyle: {
        fontSize: "12px",
        fontWeight: "normal",
      },
      labelFormat: "{name}",
      y: 40,
    },
    plotOptions: {
      pie: {
        size: "100%",
        innerSize: "70%",
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.0f}%",
        },
        showInLegend: true,
      },
    },
    tooltip: {
      pointFormat: "{point.name}: <b>{point.percentage:.0f}%</b>",
    },
    series: [
      {
        name: "Status",
        data: [
          { name: "Reported", y: dataStatus.reported, color: "#407775" },
          { name: "Not Reported", y: dataStatus.notMaterial, color: "#fab430" },
          { name: "To Do", y: dataStatus.toDo, color: "#B4C9C8" },
        ],
      },
    ],
  };

  const dataCollectionChartOptions = {
    chart: {
      type: "bar",
      height: 150,
    },
    title: {
      text: "Data Collection",
      align: "left",
    },
    xAxis: {
      categories: ["Quantitative", "Qualitative"],
      lineWidth: 0,
      title: {
        text: null,
      },
    },
    yAxis: {
      title: {
        text: null,
      },
      lineWidth: 0,
      labels: {
        formatter: function () {
          return this.value + "%";
        },
      },
      max: 50,
      tickInterval: 10,
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        pointPadding: 0.1,
        groupPadding: 0.5,
        borderWidth: 0,
        pointWidth: 20,
      },
    },
    series: [
      {
        name: "Data",
        data: [
          { y: 65, name: "65/100", color: "#ADA9A9" },
          { y: 35, name: "35/100", color: "#FAB430" },
        ],
      },
    ],
  };

  const quantitativeDataSourcesOptions = {
    chart: {
      type: "pie",
      height: 200,
      events: {
        render: function () {
          const chart = this;
          const centerX = chart.plotWidth / 1.8 + chart.plotLeft;
          const centerY = chart.plotHeight / 2 + chart.plotTop;

          if (chart.customLabel) chart.customLabel.destroy();

          chart.customLabel = chart.renderer
            .text("820", centerX, centerY)
            .css({
              color: "#000",
              fontSize: "16px",
              fontWeight: "500",
              textAlign: "right",
            })
            .attr({
              align: "center",
            })
            .add()
            .toFront();

          chart.customLabel.attr({
            x: centerX - chart.customLabel.getBBox().width / 2,
            y: centerY + chart.customLabel.getBBox().height / 4,
          });
        },
      },
    },
    title: {
      text: "Quantitative Data Sources",
      align: "left",
      margin: 30,
    },
    credits: {
      enabled: false,
    },
    legend: {
      align: "left",
      verticalAlign: "top",
      layout: "vertical",
      itemStyle: {
        fontSize: "12px",
        fontWeight: "normal",
      },
      labelFormat: "{name}",
      y: 40,
    },
    plotOptions: {
      pie: {
        size: "100%",
        innerSize: "70%",
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.0f}%",
        },
        showInLegend: true,
      },
    },
    tooltip: {
      pointFormat: "{point.name}: <b>{point.percentage:.0f}%</b>",
    },
    series: [
      {
        name: "Status",
        data: [
          { name: "Automated inputs", y: quantitativeDataSources.automatedInputs, color: "#407775" },
          { name: "Manual Inputs", y: quantitativeDataSources.manualInputs, color: "#fab430" },
          { name: "Not available", y: quantitativeDataSources.notAvailable, color: "#B4C9C8" },
        ],
      },
    ],
  };

  const esgStatusChartOptions = {
    chart: {
      type: "bar",
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "Data Status for ESG",
      align: "left",
    },
    xAxis: {
      categories: ["To Do", "Not Reported", "Reported"],
      lineWidth: 0,
    },
    yAxis: {
      title: {
        text: null,
      },
      lineWidth: 0,
      labels: {
        enabled: true,
      },
    },
    plotOptions: {
      bar: {
        pointPadding: 0,
        borderWidth: 0,
        borderRadius: 0,
      },
    },
    series: [
      {
        name: "Section A",
        data: [esgBreakdown.SectionA.toDo, esgBreakdown.SectionA.notMaterial, esgBreakdown.SectionA.reported],
        color: "#B4C9C8",
      },
      {
        name: "Section B",
        data: [esgBreakdown.SectionB.toDo, esgBreakdown.SectionB.notMaterial, esgBreakdown.SectionB.reported],
        color: "#fab430",
      },
      {
        name: "Section C",
        data: [esgBreakdown.SectionC.toDo, esgBreakdown.SectionC.notMaterial, esgBreakdown.SectionC.reported],
        color: "#407775",
      },
      {
        name: "Governance",
        data: [esgBreakdown.Governance.toDo, esgBreakdown.Governance.notMaterial, esgBreakdown.Governance.reported],
        color: "#A9A9A9",
      },
    ],
  };

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="row">
            <div className="steps__containt-hader col-md-6">
              <h1 className="title">BRSR Report 2026</h1>
              <div role="presentation">
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                  <Link underline="hover" color="inherit" href="/standards">
                    Reports
                  </Link>
                  <Link underline="hover" color="inherit" href="/standards">
                    Standards & Frameworks
                  </Link>
                  <Link underline="hover" color="inherit" href="/brsr/new-report">
                    BRSR
                  </Link>
                  <Typography color="text.primary">{savedReportName}</Typography>
                </Breadcrumbs>
              </div>
            </div>
            <div className="stepper-box col-md-6">
              <Stepper activeStep={3} alternativeLabel className="brsr-stepper">
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel className="step-name">{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </div>
          </div>

          <div className="button-box d-flex justify-content-end mb-4">
            <Button onClick={() => navigate(-1)} variant="outlined" className="module-btn" size="medium">
              <WestIcon fontSize="small" sx={{ marginRight: "4px" }} />
              Back
            </Button>
          </div>

          <div className="report-dashboard pt-6">
            <div className="row mb-4">
              <div className="col-md-6 mb-3">
                {/* First Card: BRSR Report */}
                <div className="card mb-3 title-esrs">
                  <div className="card-body">
                    <h6 className="card-subtitle text-muted mb-2">BRSR</h6>
                    <h4 className="card-title">{reportInfo.reportName}</h4>
                  </div>
                </div>

                {/* Second Card: Completion By */}
                <div className="card title-esrs">
                  <div className="card-body">
                    <h6 className="card-subtitle text-muted mb-2">Completion By</h6>
                    <h4 className="card-title">{reportInfo.completionDate}</h4>
                  </div>
                </div>
              </div>

              {/* Data Points Status Chart */}
              <div className="col-md-6 mb-3">
                <div className="card shadow">
                  <div className="card-body">
                    <HighchartsReact highcharts={Highcharts} options={dataPointsChartOptions} />
                  </div>
                </div>
              </div>

              {/* Data Status for ESG Chart */}
              <div className="col-md-6">
                <div className="card shadow">
                  <div className="card-body">
                    <HighchartsReact highcharts={Highcharts} options={esgStatusChartOptions} />
                  </div>
                </div>
              </div>

              {/* Data Collection & Quantitative Sources */}
              <div className="col-md-6">
                <div className="card shadow mb-3">
                  <div className="card-body">
                    <HighchartsReact highcharts={Highcharts} options={dataCollectionChartOptions} />
                  </div>
                </div>
                <div className="card shadow">
                  <div className="card-body">
                    <HighchartsReact highcharts={Highcharts} options={quantitativeDataSourcesOptions} />
                  </div>
                </div>
              </div>
            </div>

            {/* Sections and Principles with Detailed Breakdown */}
            <div className="row main-factors">
              {/* Left side: Section A & Section B */}
              <div className="col-md-6">
                <ul className="list-group">
                  <li className="list-group-item mb-4">
                    <div className="factor-title">Section A: General Disclosures</div>
                    <div className="factor-item">
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                        <label className="link-label">Details of the listed entity</label>
                        <div className="d-flex align-items-center">
                          <span className="me-4 question-count">0/6</span>
                          <div className="progress" style={{ width: "100px" }}>
                            <div className="progress-bar" style={{ width: "0%", background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)", height: "100%" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="factor-item">
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                        <label className="link-label">Products/services</label>
                        <div className="d-flex align-items-center">
                          <span className="me-4 question-count">0/2</span>
                          <div className="progress" style={{ width: "100px" }}>
                            <div className="progress-bar" style={{ width: "0%", background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)", height: "100%" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="factor-item">
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                        <label className="link-label">Operations</label>
                        <div className="d-flex align-items-center">
                          <span className="me-4 question-count">0/2</span>
                          <div className="progress" style={{ width: "100px" }}>
                            <div className="progress-bar" style={{ width: "0%", background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)", height: "100%" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="factor-item">
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                        <label className="link-label">Employees</label>
                        <div className="d-flex align-items-center">
                          <span className="me-4 question-count">0/3</span>
                          <div className="progress" style={{ width: "100px" }}>
                            <div className="progress-bar" style={{ width: "0%", background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)", height: "100%" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="factor-item">
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                        <label className="link-label">Holding, Subsidiary & Associate Companies</label>
                        <div className="d-flex align-items-center">
                          <span className="me-4 question-count">0/1</span>
                          <div className="progress" style={{ width: "100px" }}>
                            <div className="progress-bar" style={{ width: "0%", background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)", height: "100%" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="factor-item">
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                        <label className="link-label">CSR Details</label>
                        <div className="d-flex align-items-center">
                          <span className="me-4 question-count">0/1</span>
                          <div className="progress" style={{ width: "100px" }}>
                            <div className="progress-bar" style={{ width: "0%", background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)", height: "100%" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="factor-item">
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                        <label className="link-label">Transparency and Disclosures Compliances</label>
                        <div className="d-flex align-items-center">
                          <span className="me-4 question-count">0/2</span>
                          <div className="progress" style={{ width: "100px" }}>
                            <div className="progress-bar" style={{ width: "0%", background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)", height: "100%" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>

                <ul className="list-group">
                  <li className="list-group-item mb-4">
                    <div className="factor-title">Section B: Management And Process Disclosures</div>
                    <div className="factor-item">
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                        <label className="link-label">Policy and management processes</label>
                        <div className="d-flex align-items-center">
                          <span className="me-4 question-count">4/6</span>
                          <div className="progress" style={{ width: "100px" }}>
                            <div className="progress-bar" style={{ width: "67%", background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)", height: "100%" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="factor-item">
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                        <label className="link-label">Governance, leadership and oversight</label>
                        <div className="d-flex align-items-center">
                          <span className="me-4 question-count">4/6</span>
                          <div className="progress" style={{ width: "100px" }}>
                            <div className="progress-bar" style={{ width: "67%", background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)", height: "100%" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Right side: Section C */}
              <div className="col-md-6">
                <ul className="list-group">
                  <li className="list-group-item mb-4">
                    <div className="factor-title">Section C: Principle-Wise Performance Disclosures</div>
                    {BRSR_ACCORDION_SECTIONS.map((principle, index) => (
                      <div className="factor-item" key={index}>
                        <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                          <label className="link-label">{principle}</label>
                          <div className="d-flex align-items-center">
                            <span className="me-4 question-count">12/9</span>
                            <div className="progress" style={{ width: "100px" }}>
                              <div className="progress-bar" style={{ width: "100%", background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)", height: "100%" }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
